'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { Link } from '@/i18n/routing';
import { Check, Copy, Download, Loader2 } from 'lucide-react';

import { Latency, countryFlag } from '@/components/datahunter/proxy-table';

/**
 * Проверка прокси, которые принёс посетитель.
 *
 * Сама проверка идёт на сервере (`/api/proxy-check` → API): из браузера
 * нельзя ни открыть SOCKS-соединение, ни узнать, с какого адреса запрос
 * пришёл на ту сторону. Здесь только ввод, раздача пачками и таблица.
 *
 * Пределы на клиенте повторяют пределы API, а не заменяют их: API всё
 * равно отрежет лишнее. Повторяем ради того, чтобы человек узнал о
 * пределе до нажатия, а не получил отказ на всю пачку целиком.
 */

/** Сколько строк проверяется без входа. */
const GUEST_MAX = 10;
/** Сколько строк проверяется за раз после входа. */
const USER_MAX = 500;
/** Строк в одном запросе: столько API принимает за раз. */
const BATCH = 10;
/** Сколько запросов идёт одновременно. Больше — и сервер проверки
 *  будет занят одним человеком, а выигрыш в скорости уже невелик. */
const PARALLEL = 3;

/** Строка ответа API. */
interface CheckResult {
  input: string;
  proxy: string;
  ok: boolean;
  protocol: string;
  latency: number;
  exit_ip: string;
  country: string;
  city: string;
  anonymity: string;
  https: boolean;
  error: string;
}

interface CheckResponse {
  results: CheckResult[];
}

/** Отказ API целиком, а не по отдельной строке. */
class CheckError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

const ANONYMITY = new Set(['transparent', 'anonymous', 'elite', 'unknown']);
const ROW_ERRORS = new Set(['bad_format', 'private_address', 'no_answer']);

/** Ключ текста для отказа API. Неизвестный код — общая фраза. */
function errorKey(code: string): string {
  switch (code) {
    case 'invalid_request':
      return 'error_invalid_request';
    case 'guest_quota_exceeded':
      return 'error_guest_quota';
    case 'quota_exceeded':
      return 'error_quota';
    case 'checker_unavailable':
      return 'error_unavailable';
    default:
      return 'error_generic';
  }
}

/** Один запрос к посреднику. */
async function checkBatch(proxies: string[]): Promise<CheckResult[]> {
  const response = await fetch('/api/proxy-check', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ proxies }),
  });

  if (!response.ok) {
    // Тело отказа может оказаться не JSON (страница ошибки прокси перед
    // API), и разбор не должен заслонять настоящую причину.
    let code = '';
    try {
      const body = (await response.json()) as { error?: { code?: string } };
      code = body.error?.code ?? '';
    } catch {
      // код останется пустым — покажем общую фразу
    }
    throw new CheckError(code);
  }

  return ((await response.json()) as CheckResponse).results;
}

export function ProxyChecker() {
  const t = useTranslations('dh_proxy_check_ui');
  const { status } = useSession();
  const signedIn = status === 'authenticated';

  const [text, setText] = useState('');
  const [results, setResults] = useState<CheckResult[]>([]);
  const [running, setRunning] = useState(false);
  const [total, setTotal] = useState(0);
  /** Сколько строк отрезано пределом в последнем запуске. */
  const [cut, setCut] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  /** Номер запуска: ответы прошлого запуска не должны попасть в новый. */
  const runId = useRef(0);

  const lines = useMemo(
    () =>
      text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== ''),
    [text],
  );

  const run = useCallback(async () => {
    setError('');
    if (lines.length === 0) {
      setError('empty_input');
      return;
    }

    const max = signedIn ? USER_MAX : GUEST_MAX;
    const todo = lines.slice(0, max);
    setCut(lines.length > max);

    const id = ++runId.current;
    setResults([]);
    setTotal(todo.length);
    setRunning(true);

    const batches: string[][] = [];
    for (let i = 0; i < todo.length; i += BATCH) batches.push(todo.slice(i, i + BATCH));

    // Пачки раздаются нескольким работникам по очереди. Результаты
    // дописываются по мере прихода: при пятистах строках ждать конца
    // всей проверки молча было бы минутой пустого экрана.
    let next = 0;
    let stopped = false;
    const worker = async () => {
      while (!stopped && next < batches.length) {
        const batch = batches[next++] ?? [];
        try {
          const part = await checkBatch(batch);
          if (runId.current !== id) return;
          setResults((prev) => [...prev, ...part]);
        } catch (cause) {
          if (runId.current !== id) return;
          // Отказ API (предел на сегодня, сервис недоступен) повторится
          // и на следующих пачках: продолжать значило бы показать одну
          // и ту же ошибку несколько раз и сжечь остаток предела.
          stopped = true;
          setError(errorKey(cause instanceof CheckError ? cause.code : 'network'));
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(PARALLEL, batches.length) }, worker));
    if (runId.current === id) setRunning(false);
  }, [lines, signedIn]);

  // Выгружается то, что человек ввёл сам, а не нормализованный адрес:
  // в строке ввода остаются его логин и пароль, а ответ API их намеренно
  // не содержит. Всё это происходит только в его браузере.
  const working = useMemo(() => results.filter((r) => r.ok).map((r) => r.input), [results]);

  const copyWorking = useCallback(() => {
    void navigator.clipboard.writeText(working.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    });
  }, [working]);

  const downloadWorking = useCallback(() => {
    const blob = new Blob([`${working.join('\n')}\n`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'proxies.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [working]);

  const quotaForGuest = error === 'error_guest_quota';

  return (
    <div className="max-w-[1280px] mx-auto px-6 pb-16">
      <div className="max-w-3xl">
        <label htmlFor="proxy-check-input" className="block text-sm font-medium mb-2">
          {t('input_label')}
        </label>
        <textarea
          id="proxy-check-input"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          placeholder={t('placeholder')}
          rows={8}
          spellCheck={false}
          autoComplete="off"
          className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] font-mono text-sm outline-none focus:border-[var(--color-text-muted)]"
        />
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => {
              void run();
            }}
            disabled={running}
            className="dh-cta inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-colors disabled:opacity-60"
          >
            {running && <Loader2 className="w-4 h-4 animate-spin" />}
            {running ? t('checking') : t('check')}
          </button>
          <span className="text-sm text-[var(--color-text-muted)]">
            {t('lines', { count: lines.length })}
          </span>
          {total > 0 && (
            <span
              role="status"
              aria-live="polite"
              className="text-sm text-[var(--color-text-muted)]"
            >
              {running
                ? t('progress', { done: results.length, total })
                : t('summary', { ok: working.length, total: results.length })}
            </span>
          )}
        </div>
      </div>

      {cut && !signedIn && (
        <SignInNotice text={t('guest_limit', { guest: GUEST_MAX, max: USER_MAX })} />
      )}
      {cut && signedIn && (
        <p className="mt-4 text-sm text-[var(--color-text-muted)]">
          {t('user_limit', { max: USER_MAX })}
        </p>
      )}

      {error !== '' &&
        (quotaForGuest ? (
          <SignInNotice text={t(error)} />
        ) : (
          <p role="alert" className="mt-4 text-sm text-[var(--color-accent-400)]">
            {t(error)}
          </p>
        ))}

      {results.length > 0 && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <button
              type="button"
              onClick={copyWorking}
              disabled={working.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm disabled:opacity-40 hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? t('copied') : t('copy_working')}
            </button>
            <button
              type="button"
              onClick={downloadWorking}
              disabled={working.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm disabled:opacity-40 hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {t('download_txt')}
            </button>
          </div>

          {/* `overflow-x-auto`: таблица шире телефона, прокручиваться
              должна она, а не вся страница (см. ProxyTable). */}
          <div className="overflow-x-auto border border-[var(--color-border)] rounded-2xl bg-[var(--color-bg-card)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left">
                  {(
                    [
                      'col_status',
                      'col_proxy',
                      'col_protocol',
                      'col_latency',
                      'col_exit',
                      'col_anonymity',
                      'col_https',
                    ] as const
                  ).map((key) => (
                    <th key={key} scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                      {t(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Ключ с номером: одну строку могли вставить дважды. */}
                {results.map((r, index) => (
                  <ResultRow key={`${r.input}-${String(index)}`} result={r} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** Строка таблицы результатов. */
function ResultRow({ result: r }: { result: CheckResult }) {
  const t = useTranslations('dh_proxy_check_ui');

  if (!r.ok) {
    const reason = ROW_ERRORS.has(r.error) ? `err_${r.error}` : 'err_unknown';
    return (
      <tr className="border-b border-[var(--color-border)] last:border-0">
        <td className="px-4 py-2.5 whitespace-nowrap text-[var(--color-text-muted)]">
          {t('status_fail')}
        </td>
        <td className="px-4 py-2.5 font-mono whitespace-nowrap">{r.proxy || r.input}</td>
        <td colSpan={5} className="px-4 py-2.5 text-[var(--color-text-muted)]">
          {t(reason)}
        </td>
      </tr>
    );
  }

  const anonymity = ANONYMITY.has(r.anonymity) ? r.anonymity : 'unknown';
  const city = r.city !== '' && r.city.toLowerCase() !== 'unknown' ? r.city : '';

  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-bg-elevated)] transition-colors">
      <td className="px-4 py-2.5 whitespace-nowrap text-emerald-500">{t('status_ok')}</td>
      <td className="px-4 py-2.5 font-mono whitespace-nowrap">{r.proxy || r.input}</td>
      <td className="px-4 py-2.5 whitespace-nowrap">{r.protocol}</td>
      <td className="px-4 py-2.5 whitespace-nowrap">
        <Latency ms={r.latency} />
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap">
        <span className="font-mono">{r.exit_ip}</span>
        {r.country !== '' && (
          <span>
            {' '}
            {countryFlag(r.country)} {r.country}
          </span>
        )}
        {city !== '' && <span className="text-[var(--color-text-muted)]"> · {city}</span>}
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap">{t(`anonymity_${anonymity}`)}</td>
      <td className="px-4 py-2.5 whitespace-nowrap">{r.https ? t('yes') : t('no')}</td>
    </tr>
  );
}

/**
 * Предложение войти. Устроено как у списка прокси (`MaskedNotice`):
 * ссылка, а не кнопка, и локализованная навигация.
 */
function SignInNotice({ text }: { text: string }) {
  const t = useTranslations('dh_proxy_check_ui');

  return (
    <div className="mt-6 max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] px-5 py-4 flex flex-wrap items-center justify-between gap-4">
      <p className="text-sm text-[var(--color-text-muted)] max-w-xl">{text}</p>
      <Link
        href="/login"
        className="dh-cta px-5 py-2 rounded-full text-sm font-medium transition-colors"
      >
        {t('sign_in')}
      </Link>
    </div>
  );
}
